import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { tap } from 'rxjs';
import { Channel } from 'pusher-js';
import { PusherService } from '../pusher/pusher.service';
import { TenantService } from '../tenant/tenant.service';
import { ENDPOINTS } from '../../constants/endpoints.constants';
import { REALTIME_CHANNELS } from '../../constants/realtime-channels.constants';
import { TabInsightResponse } from '../../models/notification-tab-insight.model';
import { AuthService } from '../auth/auth.service';
import { NotificationResponse } from '../../../../ui-components/lib/notification/notifications-type.enum';

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private readonly http = inject(HttpClient);
  private readonly pusherService = inject(PusherService);
  private readonly tenantService = inject(TenantService);
  private readonly authService = inject(AuthService);

  markAsReadTrigger = signal(0);
  newNotificationTrigger = signal(0);
  private readonly channel = signal<Channel | null>(null);
  private readonly channelName = signal<string>('');

  getNotifications(
    unreadOnly?: boolean,
    cursor?: string | null,
    perPage?: number
  ) {
    let params = new HttpParams();
    if (perPage !== undefined) {
      params = params.set('per_page', perPage);
    }
    if (unreadOnly) {
      params = params.set('filters[unread_only]', '');
    }
    if (cursor) {
      params = params.set('cursor', cursor);
    }
    return this.http.get<NotificationResponse>(ENDPOINTS.Notifications.List, {
      params,
    });
  }

  getTabsInsight() {
    return this.http.get<TabInsightResponse>(
      ENDPOINTS.Notifications.TabsInsight
    );
  }

  markAsRead(notificationId: number) {
    const url = ENDPOINTS.Notifications.MarkRead.replace(
      '{notificationId}',
      notificationId.toString()
    );
    return this.http
      .patch(url, {})
      .pipe(tap(() => this.markAsReadTrigger.update(v => v + 1)));
  }

  markAllAsRead() {
    return this.http
      .patch(ENDPOINTS.Notifications.MarkAllRead, {})
      .pipe(tap(() => this.markAsReadTrigger.update(v => v + 1)));
  }

  subscribeToNotifications(): void {
    const user = this.authService.centralUser() || this.authService.user();
    const userId = user?.id.toString();
    if (!userId) {
      return;
    }
    const channelName = REALTIME_CHANNELS.NOTIFICATION.replace(
      '{tenant}',
      this.tenantService.getTenant()
    ).replace('{userId}', userId);
    this.channelName.set(channelName);

    const channel = this.pusherService.subscribe(this.channelName());
    this.channel.set(channel);

    this.channel()?.bind('notification.created', () => {
      this.newNotificationTrigger.update(v => v + 1);
    });
  }

  unsubscribeFromNotifications(): void {
    if (this.channelName()) {
      this.pusherService.unsubscribe(this.channelName());
      this.channel.set(null);
    }
  }
}
